import React, { useEffect, useRef, useState } from 'react';
import { FormikValues } from 'formik';
import styled from 'styled-components';

import { Card, FormHelperText, Grid, IconButton } from '@material-ui/core';
import {
  Assignment as AssignmentIcon,
  Info as InfoIcon
} from '@material-ui/icons';
import { InlineForm, InlineFormik } from '@sierralabs/react-material-ui-formik';

import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import CardTitleContent from '../../../common/card/CardTitleContent';
import InlineSelect from '../../../common/inline/InlineSelect';
import InlineTextField from '../../../common/inline/InlineTextField';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { getRiskLabel } from '../Hazard.helpers';
import { useUpdateHazard } from '../Hazard.hooks';
import {
  BaseHazardCardProps,
  Hazard,
  hazardProbability,
  hazardSeverity,
  HazardSeverityTable
} from '../Hazard.interface';
import { HazardCardInfoDialog } from '../dialogs/HazardCardInfoDialog';

const StyledCard = styled(Card)`
  .form-control {
    width: 150px;
  }
`;

const HazardInitialAssessmentCard: React.FC<BaseHazardCardProps> = props => {
  const { hazard, hazardVersion, onChange } = props;
  const [showInfo, setShowInfo] = useState(false);
  const { updatedHazard, isUpdating, updateError, updateHazard } =
    useUpdateHazard(hazard.id);
  const formikRef = useRef<FormikValues>();
  const onChangeRef = useRef(onChange);
  const { isFieldDisabled } = useTaskEntityFields(hazard.task);
  const disabled = !!hazardVersion;
  const data = hazardVersion || hazard;

  useEffect(() => {
    if (updatedHazard) {
      onChangeRef.current?.(updatedHazard);
    }
  }, [updatedHazard]);
  return (
    <StyledCard>
      <CardTitleContent>
        <CardTitle>
          <AssignmentIcon />
          Hazard Initial Assessment
        </CardTitle>
        <IconButton size='small' onClick={() => setShowInfo(true)}>
          <InfoIcon />
        </IconButton>
      </CardTitleContent>
      <CardContent padding='large'>
        <InlineFormik
          error={updateError}
          isSubmitting={isUpdating}
          innerRef={(formik: any) => (formikRef.current = formik)}
          initialValues={{
            initialSeverity: data.initialSeverity,
            initialProbability: data.initialProbability,
            initialRpn: data.initialRpn,
            initialRiskLabel: getRiskLabel(data.initialRpn)
          }}
          onSubmit={(dto: Partial<Hazard>) => updateHazard(dto)}
        >
          <InlineForm>
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12} lg={4}>
                <FormHelperText>Initial Severity</FormHelperText>
                <InlineSelect
                  name='initialSeverity'
                  label=''
                  disabled={disabled || isFieldDisabled('initialSeverity')}
                  options={hazardSeverity.map(s => ({
                    value: s.value,
                    label: `${s.value} - ${s.label}`
                  }))}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <FormHelperText>Initial Probability</FormHelperText>
                <InlineSelect
                  name='initialProbability'
                  label=''
                  disabled
                  options={[
                    ...hazardProbability.map(s => ({
                      value: s.value,
                      label: `${s.value} - ${s.label}`
                    })),
                    { value: 0, label: '' }
                  ]}
                />
                <FormHelperText>
                  Calculated based on hazard causes
                </FormHelperText>
              </Grid>
              <Grid item xs={12} lg={2}>
                <FormHelperText>Initial RPN</FormHelperText>
                <InlineTextField disabled name='initialRpn' />
                <FormHelperText>
                  Calculated based on severity and probability
                </FormHelperText>
              </Grid>
              <Grid item xs={12} lg={2}>
                <FormHelperText>Initial Risk</FormHelperText>
                <InlineTextField name='initialRiskLabel' disabled />
              </Grid>
            </Grid>
          </InlineForm>
        </InlineFormik>
      </CardContent>
      {showInfo && (
        <HazardCardInfoDialog
          title='Hazard Severity Table'
          table={HazardSeverityTable}
          onClose={() => setShowInfo(false)}
        />
      )}
    </StyledCard>
  );
};

export default HazardInitialAssessmentCard;
